// Helpers for deriving overview views (totals, sparklines, deltas) from the
// monthly bucket payload returned by POST /movements/group with dimensions
// ['year', 'month', 'currency', 'type', 'account_id'].
//
// Buckets look like: { year, month, currency, type, account_id, account_name, total, num_movements }
// where `total` is signed (Income positive, Expenses negative).

export const RANGES = ['1W', '1M', '3M', 'YTD', '1Y', 'ALL'];

/**
 * Bucket width each range asks the backend for (MI-307).
 *
 * Monthly buckets leave 1W/1M with 2–3 points, which draws as a flat segment.
 * Short ranges therefore fetch a finer grain; the long ones stay monthly so the
 * cached full-history payload keeps serving them with zero network.
 */
export const GRANULARITY_BY_RANGE = {
  '1W':  'day',
  '1M':  'day',
  '3M':  'day',
  'YTD': 'week',
  '1Y':  'month',
  'ALL': 'month',
};

export function granularityForRange(range) {
  return GRANULARITY_BY_RANGE[range] || 'month';
}

/**
 * Bucket width for an explicit [startDate, endDate] window (the Dashboard's
 * date filter, MI-240): daily up to three months, weekly beyond. All time
 * (no start) stays monthly, read from the cached full-history payload. A dated
 * window never goes monthly: a month bucket counts its whole month, so a
 * window ending mid-month would end on a later balance.
 */
export function granularityForWindow(startDate, endDate) {
  if (!startDate || !endDate) return 'month';
  const days = (new Date(`${endDate}T00:00:00`) - new Date(`${startDate}T00:00:00`)) / 86400000;
  return days <= 92 ? 'day' : 'week';
}

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' in local time — avoids the UTC shift of toISOString(). */
export function toISODate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** First day of the bucket `d` falls in. Mirrors the backend's date_trunc. */
export function snapToPeriod(d, granularity) {
  const date = typeof d === 'string' ? new Date(`${d}T00:00:00`) : new Date(d);
  if (granularity === 'day') return toISODate(date);
  if (granularity === 'week') {
    // ISO week: Monday → Sunday. getDay() is 0 for Sunday.
    const offset = (date.getDay() + 6) % 7;
    date.setDate(date.getDate() - offset);
    return toISODate(date);
  }
  date.setDate(1);
  return toISODate(date);
}

/**
 * Inclusive [startDate, endDate] in ISO days for a range — the day-precision
 * counterpart of `getRangeWindow`, which snaps to whole months and therefore
 * cannot express "the last 7 days". `ALL` has no start.
 */
export function getRangeDateWindow(range, now = new Date()) {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  switch (range) {
    case '1W':  start.setDate(start.getDate() - 6); break;
    case '1M':  start.setMonth(start.getMonth() - 1); break;
    case '3M':  start.setMonth(start.getMonth() - 3); break;
    case 'YTD': start.setMonth(0); start.setDate(1); break;
    case '1Y':  start.setFullYear(start.getFullYear() - 1); break;
    case 'ALL': return { startDate: null, endDate: toISODate(end) };
    default:    start.setMonth(start.getMonth() - 1);
  }
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

/**
 * Ordered list of ISO period keys covering [startDate, endDate] inclusive, at
 * the given granularity. Period keys are the bucket's first day, so they match
 * what the backend emits and can be compared as strings.
 */
export function getPeriodsInWindow(startDate, endDate, granularity) {
  if (!startDate || !endDate) return [];
  const cursor = new Date(`${snapToPeriod(startDate, granularity)}T00:00:00`);
  const last = new Date(`${endDate}T00:00:00`);
  const periods = [];
  while (cursor <= last) {
    periods.push(toISODate(cursor));
    if (granularity === 'day') cursor.setDate(cursor.getDate() + 1);
    else if (granularity === 'week') cursor.setDate(cursor.getDate() + 7);
    else cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
}

/**
 * The last day a period key's balance counts (its day, its week's Sunday, its
 * month's last day), never after `today`: the day a point of a balance series
 * stands for.
 */
export function periodEnd(period, granularity, today = toISODate(new Date())) {
  const date = new Date(`${period}T00:00:00`);
  if (granularity === 'week') date.setDate(date.getDate() + 6);
  else if (granularity !== 'day') date.setMonth(date.getMonth() + 1, 0);
  const end = toISODate(date);
  return end > today ? today : end;
}

function startOfMonth(year, month) {
  return new Date(year, month - 1, 1);
}

function ymKey(year, month) {
  return `${year}-${pad(month)}`;
}

/**
 * Returns the inclusive [start, end] window in {year, month} for the given range,
 * plus the previous comparable window for delta calculations.
 *
 * Buckets are monthly so windows snap to month boundaries — 1W collapses to the
 * current month (we don't have day-level granularity in this payload).
 */
export function getRangeWindow(range, now = new Date()) {
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  const end = { year: cy, month: cm };

  let monthsBack;
  switch (range) {
    case '1W':
    case '1M': monthsBack = 1; break;
    case '3M': monthsBack = 3; break;
    case '1Y': monthsBack = 12; break;
    case 'YTD': monthsBack = cm; break;
    case 'ALL': monthsBack = null; break;
    default: monthsBack = 1;
  }

  if (monthsBack === null) {
    return { start: null, end, prevStart: null, prevEnd: null };
  }

  const startDate = new Date(cy, cm - monthsBack, 1);
  const start = { year: startDate.getFullYear(), month: startDate.getMonth() + 1 };

  const prevEndDate = new Date(start.year, start.month - 2, 1);
  const prevStartDate = new Date(prevEndDate.getFullYear(), prevEndDate.getMonth() - monthsBack + 1, 1);
  const prevStart = { year: prevStartDate.getFullYear(), month: prevStartDate.getMonth() + 1 };
  const prevEnd = { year: prevEndDate.getFullYear(), month: prevEndDate.getMonth() + 1 };

  return { start, end, prevStart, prevEnd };
}

/**
 * Returns ordered list of {year, month} keys covering [start, end] inclusive.
 * If start is null (range='ALL'), returns months found in buckets.
 */
export function getMonthsInWindow(start, end, allBuckets = []) {
  if (!start) {
    const set = new Set(allBuckets.map(b => ymKey(b.year, b.month)));
    return [...set]
      .map(k => { const [y, m] = k.split('-').map(Number); return { year: y, month: m }; })
      .sort((a, b) => a.year - b.year || a.month - b.month);
  }
  const months = [];
  let cur = startOfMonth(start.year, start.month);
  const endDate = startOfMonth(end.year, end.month);
  while (cur <= endDate) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return months;
}

export function bucketInWindow(bucket, start, end) {
  if (!start) return true;
  const k = bucket.year * 100 + bucket.month;
  return k >= start.year * 100 + start.month && k <= end.year * 100 + end.month;
}

/**
 * Computes percent change between current and previous totals.
 * Returns null when previous is 0 (delta is undefined) or when the result
 * exceeds a sanity cap — a tiny baseline produces astronomical percentages
 * that aren't meaningful to the user.
 */
const DELTA_PCT_CAP = 999;
export function computeDeltaPct(current, previous) {
  if (!previous) return null;
  const pct = ((current - previous) / previous) * 100;
  if (!Number.isFinite(pct) || Math.abs(pct) > DELTA_PCT_CAP) return null;
  return pct;
}

/**
 * Converts a numeric series to an SVG path string scaled to the given box.
 * Used by AccountSparkline and DualBalanceChart.
 */
export function valuesToSparkPath(values, width = 64, height = 16) {
  if (!values || values.length === 0) return '';
  if (values.length === 1) return `M0,${(height / 2).toFixed(1)} L${width},${(height / 2).toFixed(1)}`;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);

  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * Sum absolute totals from buckets matching a predicate.
 */
export function sumAbs(buckets, predicate = () => true) {
  return buckets.reduce((s, b) => s + (predicate(b) ? Math.abs(b.total || 0) : 0), 0);
}

/**
 * Cumulative signed balance for a single account at the end of each month.
 * Aligned to `months[]` — same length, same order.
 *
 * Income contributes positively, Expenses negatively (matches `b.total` sign
 * across the codebase). For Debt-style accounts (Credit/Loan) the cumulative
 * is negative ("you owe X"); the display layer is responsible for `Math.abs`.
 *
 * Includes ALL history before `months[0]` in the running total — the visible
 * window is just where we sample the cumulative line, not where accumulation
 * starts.
 */
export function buildAccountBalanceSeries(buckets, accountId, months, currency) {
  if (!accountId || !months || months.length === 0) return [];
  const periods = months.map(m => `${m.year}-${pad(m.month)}-01`);
  return buildBalanceSeries(buckets, accountId, periods, currency, 'month');
}

/** ISO period key of a bucket, whichever shape the payload carries. */
function bucketPeriodKey(bucket) {
  if (bucket.period) return String(bucket.period).slice(0, 10);
  if (bucket.year && bucket.month) return `${bucket.year}-${pad(bucket.month)}-01`;
  return null;
}

/**
 * Cumulative signed balance for one account, sampled at each ISO period key in
 * `periods` (which must be sorted ascending). Same semantics as
 * `buildAccountBalanceSeries` — this is the general, granularity-agnostic form.
 *
 * Single pass: buckets are summed per period once, then periods and bucket keys
 * are walked together with a running total. The previous implementation
 * re-scanned every earlier bucket at each sampled point, which is invisible at
 * 4 points and wasteful at 90+.
 *
 * Bucket keys are re-snapped to `granularity`, so a weekly view can be built
 * from daily buckets without a second request.
 */
export function buildBalanceSeries(buckets, accountId, periods, currency, granularity = 'month') {
  if (!accountId || !periods || periods.length === 0) return [];

  const byPeriod = new Map();
  buckets.forEach(b => {
    if (b.account_id !== accountId) return;
    if (currency && currency !== 'all' && b.currency !== currency) return;
    const key = bucketPeriodKey(b);
    if (!key) return;
    const k = snapToPeriod(key, granularity);
    byPeriod.set(k, (byPeriod.get(k) || 0) + (b.total || 0));
  });

  const keys = [...byPeriod.keys()].sort();
  const series = [];
  let running = 0;
  let i = 0;
  for (const p of periods) {
    while (i < keys.length && keys[i] <= p) {
      running += byPeriod.get(keys[i]);
      i += 1;
    }
    series.push(running);
  }
  return series;
}

/**
 * Build dual time series (assets vs expenses) per month for the trend chart.
 * Assets = Income totals, Expenses = Expense totals (both signed positive for display).
 */
export function buildDualTrendSeries(buckets, months, currency) {
  const monthIdx = {};
  months.forEach((m, i) => { monthIdx[ymKey(m.year, m.month)] = i; });

  const assets = new Array(months.length).fill(0);
  const expenses = new Array(months.length).fill(0);

  buckets.forEach(b => {
    if (currency && currency !== 'all' && b.currency !== currency) return;
    const i = monthIdx[ymKey(b.year, b.month)];
    if (i == null) return;
    if (b.type === 'Income') assets[i] += Math.abs(b.total || 0);
    else if (b.type === 'Expenses') expenses[i] += Math.abs(b.total || 0);
  });

  return { assets, expenses };
}

/**
 * Period-keyed counterpart of `buildDualTrendSeries` — same Income/Expenses
 * split, but aligned to ISO period keys instead of {year, month} pairs.
 */
export function buildDualTrendSeriesByPeriod(buckets, periods, currency, granularity = 'month') {
  const idx = {};
  periods.forEach((p, i) => { idx[p] = i; });

  const assets = new Array(periods.length).fill(0);
  const expenses = new Array(periods.length).fill(0);

  buckets.forEach(b => {
    if (currency && currency !== 'all' && b.currency !== currency) return;
    const key = bucketPeriodKey(b);
    if (!key) return;
    const i = idx[snapToPeriod(key, granularity)];
    if (i == null) return;
    if (b.type === 'Income') assets[i] += Math.abs(b.total || 0);
    else if (b.type === 'Expenses') expenses[i] += Math.abs(b.total || 0);
  });

  return { assets, expenses };
}

/**
 * Builds a running net-worth series (cumulative Income − Expenses).
 * Aligned to the same months[] order as the input series.
 *
 * Used to derive the Assets line in the overview chart and the "patrimonio"
 * shown in the Assets card. Assumes the user starts from zero — the first
 * tracked month seeds the running total.
 */
export function buildCumulativeNetSeries(income, expenses) {
  let running = 0;
  return income.map((inc, i) => {
    running += (inc || 0) - (expenses[i] || 0);
    return running;
  });
}

/**
 * Returns the index of `target` (a {year, month}) in a months array, or -1.
 * Used to align summary card values and chart slices to date boundaries.
 */
export function findMonthIdx(months, target) {
  if (!target) return -1;
  for (let i = 0; i < months.length; i++) {
    if (months[i].year === target.year && months[i].month === target.month) return i;
  }
  return -1;
}

/**
 * Discover currencies present in the bucket payload, ordered by total volume desc.
 */
export function getAvailableCurrencies(buckets) {
  const totals = {};
  buckets.forEach(b => {
    const c = b.currency || 'COP';
    totals[c] = (totals[c] || 0) + Math.abs(b.total || 0);
  });
  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .map(([c]) => c);
}
